<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
                  <div class="flex items-center justify-between">
            <div>
              <h1 class="text-3xl font-bold mb-2">My Orders</h1>
              <p class="text-orange-100 text-lg">Track your jewelry orders and their status</p>
            </div>
            <div class="flex space-x-3">
              <button
                @click="refreshOrders"
                :disabled="loading"
                class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="!loading" class="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <svg v-else class="w-5 h-5 mr-2 inline animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {{ loading ? 'Refreshing...' : 'Refresh Orders' }}
              </button>
              <router-link 
                to="/customer/catalogue" 
                class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 text-white"
              >
                Browse Collection
              </router-link>
            </div>
          </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
      <p class="mt-2 text-gray-500">Loading your orders...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8">
      <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 class="text-xl font-bold text-gray-900 mb-2">Failed to load orders</h3>
      <p class="text-gray-600 mb-4">{{ error }}</p>
      <button
        @click="loadOrders"
        class="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
      >
        Try Again
      </button>
    </div>

    <!-- Orders Content -->
    <div v-else>
      <!-- No Orders State -->
      <div v-if="orders.length === 0" class="text-center py-8">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">No orders yet</h3>
        <p class="text-gray-600 mb-4">Start shopping to see your orders here</p>
        <router-link 
          to="/customer/catalogue" 
          class="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          Browse Collection
        </router-link>
      </div>

      <!-- Orders List -->
      <div v-else class="space-y-4">
        <div
          v-for="order in orders"
          :key="order.id"
          class="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
        >
          <!-- Collapsed Order Summary -->
          <div 
            class="p-6 cursor-pointer border-b border-gray-200"
            @click="toggleOrder(order.id)"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-4">
                <div class="flex-shrink-0">
                  <button 
                    class="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg 
                      class="w-5 h-5 transition-transform duration-200"
                      :class="{ 'rotate-90': expandedOrders.includes(order.id) }"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Order #{{ order.order_no }}</h3>
                  <p class="text-gray-600 text-sm">{{ formatDate(order.created_at) }}</p>
                </div>
              </div>
              <div class="flex items-center space-x-4">
                <span 
                  class="px-3 py-1 rounded-full text-sm font-medium"
                  :class="getStatusClasses(order.status, order)"
                >
                  {{ getStatusText(order.status, order) }}
                </span>
                <div class="text-right">
                  <p class="text-gray-500 text-sm">{{ getTotalItems(order) }} items</p>
                  <p class="text-gray-500 text-xs">{{ getTotalGrossWeight(order.items) }}g gross</p>
                  <p class="text-gray-500 text-xs">{{ getTotalNetWeight(order.items) }}g net</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Expanded Order Details -->
          <div 
            v-if="expandedOrders.includes(order.id)"
            class="border-t border-gray-200"
          >
            <!-- Order Items -->
            <div class="p-6">
              <div class="space-y-4">
                <div
                  v-for="item in order.items"
                  :key="item.id"
                  class="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div class="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <img 
                      v-if="item.images && item.images.length > 0"
                      :src="getImageUrl(item.images[0])"
                      :alt="item.name"
                      class="w-full h-full object-contain rounded-lg"
                    />
                    <span v-else class="text-gray-600 font-bold text-lg">{{ item.name.charAt(0) }}</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h4 class="text-lg font-semibold text-gray-900 truncate">{{ item.name }}</h4>
                    <p class="text-gray-600 text-sm">SKU: {{ item.sku }}</p>
                    <p class="text-gray-500 text-sm">{{ item.category }} • {{ item.metal }}</p>
                  </div>
                  <div class="text-right">
                    <p class="text-gray-500 text-sm">Qty: {{ item.quantity }}</p>
                  </div>
                </div>
              </div>

              <!-- Order Summary -->
              <div class="mt-6 pt-6 border-t border-gray-200">
                <!-- Combined Totals Section -->
                <div v-if="order.items && order.items.length > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 class="font-semibold text-blue-900 mb-3">Order Summary</h4>
                  <div class="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p class="text-sm text-blue-600">Total Quantity</p>
                      <p class="text-lg font-bold text-blue-900">{{ getTotalItems(order) }}</p>
                    </div>
                    <div>
                      <p class="text-sm text-blue-600">Total Gross Weight</p>
                      <p class="text-lg font-bold text-blue-900">{{ getTotalGrossWeight(order.items) }}g</p>
                    </div>
                    <div>
                      <p class="text-sm text-blue-600">Total Net Weight</p>
                      <p class="text-lg font-bold text-blue-900">{{ getTotalNetWeight(order.items) }}g</p>
                    </div>
                  </div>
                </div>
                
                <div class="flex justify-between items-center">
                  <div>
                    <p class="text-gray-600 text-sm">We'll contact you with pricing details</p>
                  </div>
                  <div class="flex space-x-3">
                    <router-link
                      :to="`/customer/orders/${order.id}`"
                      class="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
                    >
                      View Details
                    </router-link>
                    <LoadingButton
                      v-if="order.status === 'pending' && !order.assigned_workshop_user"
                      @click="cancelOrder(order.id)"
                      :loading="orderLoadingStates[order.id]"
                      text="Cancel Order"
                      loading-text="Cancelling..."
                      variant="danger"
                      size="sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Cancel Order Confirmation Modal -->
    <ConfirmDialog
      :visible="showCancelConfirm"
      title="Cancel Order"
      message="Are you sure you want to cancel this order? This action cannot be undone."
      confirm-text="Yes, Cancel Order"
      cancel-text="Keep Order"
      @confirm="confirmCancelOrder"
      @cancel="cancelCancelOrder"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ordersAPI } from '../services/api'
import { useNotificationStore } from '@/stores/notification'
import { useUserStore } from '@/stores/user'
import LoadingButton from '@/components/LoadingButton.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { PAGINATION_LIMITS } from '../constants/pagination'


export default {
  name: 'MyOrders',
  components: {
    LoadingButton,
    ConfirmDialog
  },
  setup() {
    const router = useRouter()
    const notificationStore = useNotificationStore()
    const userStore = useUserStore()
    
    // State
    const loading = ref(false)
    const error = ref('')
    const orders = ref([])
    const pagination = ref(null)
    const activeTab = ref('placed')
    const currentPage = ref(1)
    const expandedOrders = ref([]) // Track which orders are expanded
    
    // Loading states for individual order actions
    const orderLoadingStates = ref({})
    
    // Confirmation dialog state
    const showCancelConfirm = ref(false)
    const orderToCancel = ref(null)

    // Computed
    const placedOrders = computed(() => {
      return orders.value.filter(order => order.status !== 'cancelled')
    })

    const cancelledOrders = computed(() => {
      return orders.value.filter(order => order.status === 'cancelled')
    })

    const tabs = computed(() => [
      { id: 'placed', name: 'Placed Orders', count: placedOrders.value.length },
      { id: 'wishlist', name: 'Wishlist', count: 0 },
      { id: 'cancelled', name: 'Cancelled', count: cancelledOrders.value.length }
    ])

    const filteredOrders = computed(() => {
      if (activeTab.value === 'all') {
        return orders.value
      }
      if (activeTab.value === 'placed') {
        return placedOrders.value
      }
      if (activeTab.value === 'cancelled') {
        return cancelledOrders.value
      }
      return []
    })

    // Methods
    const loadOrders = async () => {
      loading.value = true
      error.value = ''
      
      try {

        
        // Check if user is authenticated
        if (!userStore.isAuthenticated) {
          throw new Error('User not authenticated')
        }
        
        // Check if user store is initialized
        if (!userStore.isInitialized) {
          await userStore.initialize()
        }
        
        // Check if user is a workshop user (not admin)
        if (userStore.isWorkshop && !userStore.isAdmin) {
          throw new Error('Workshop users should use the workshop jobs page')
        }
        
        const response = await ordersAPI.getMyOrders({
          page: currentPage.value,
          limit: PAGINATION_LIMITS.ORDERS
        })
        orders.value = response.data.orders
        pagination.value = response.data.pagination
      } catch (err) {
        console.error('Failed to load orders:', err)
        
        if (err.message === 'User not authenticated') {
          error.value = 'Please log in to view your orders'
          notificationStore.showError('Authentication Required', 'Please log in to view your orders')
        } else if (err.message === 'Workshop users should use the workshop jobs page') {
          error.value = 'Workshop users should use the Workshop Jobs page'
          notificationStore.showError('Wrong Page', 'As a workshop user, you should use the Workshop Jobs page to view your assigned jobs')
        } else {
          error.value = err.response?.data?.message || 'Failed to load orders'
          notificationStore.showError('Error', error.value)
        }
      } finally {
        loading.value = false
      }
    }

    const changePage = (page) => {
      if (page >= 1 && page <= pagination.value.totalPages) {
        currentPage.value = page
        loadOrders()
      }
    }

    const refreshOrders = () => {
      currentPage.value = 1
      loadOrders()
    }

    const cancelOrder = async (orderId) => {
      orderToCancel.value = orderId
      showCancelConfirm.value = true
    }

    const confirmCancelOrder = async () => {
      if (!orderToCancel.value) return
      
      try {
        // Set loading state for this specific order
        orderLoadingStates.value[orderToCancel.value] = true
        
        await ordersAPI.cancelOrder(orderToCancel.value)
        await loadOrders() // Reload orders
        notificationStore.showSuccess('Order Cancelled', 'Your order has been cancelled successfully.')
      } catch (err) {
        console.error('Failed to cancel order:', err)
        const errorMessage = err.response?.data?.message || 'Failed to cancel order. Please try again.'
        notificationStore.showError('Cancellation Failed', errorMessage)
      } finally {
        // Clear loading state
        orderLoadingStates.value[orderToCancel.value] = false
        showCancelConfirm.value = false
        orderToCancel.value = null
      }
    }

    const cancelCancelOrder = () => {
      showCancelConfirm.value = false
      orderToCancel.value = null
    }

    const viewOrderDetails = (orderId) => {
      // Navigate to order details page
      router.push(`/orders/${orderId}`)
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }



    const getStatusColor = (status) => {
      const colors = {
        'pending': 'bg-yellow-400',
        'in_workshop': 'bg-blue-400',
        'qc': 'bg-purple-400',
        'completed': 'bg-green-400',
        'delivered': 'bg-green-600',
        'cancelled': 'bg-red-400'
      }
      return colors[status] || 'bg-gray-400'
    }

    const getStatusText = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, show "Pending in Workshop"
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'Pending in Workshop'
      }
      
      const texts = {
        'pending': 'Pending',
        'in_workshop': 'In Workshop',
        'qc': 'Quality Check',
        'completed': 'Completed',
        'delivered': 'Delivered',
        'cancelled': 'Cancelled'
      }
      return texts[status] || status
    }

    const getStatusDescription = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, show workshop description
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'Your order has been assigned to our workshop'
      }
      
      const descriptions = {
        'pending': 'Your order is being processed',
        'in_workshop': 'Your jewelry is being crafted',
        'qc': 'Quality check in progress',
        'completed': 'Your order is ready for delivery',
        'delivered': 'Order has been delivered',
        'cancelled': 'Order has been cancelled'
      }
      return descriptions[status] || ''
    }

    const getNextStep = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, show workshop next step
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'Work will begin soon'
      }
      
      const nextSteps = {
        'pending': 'We will contact you soon',
        'in_workshop': 'Estimated completion in 3-5 days',
        'qc': 'Final quality check in progress',
        'completed': 'Ready for pickup/delivery',
        'delivered': 'Thank you for choosing Annzz!',
        'cancelled': 'Order cancelled'
      }
      return nextSteps[status] || ''
    }

    const getStatusClasses = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, use workshop styling
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'bg-purple-400'
      }
      
      const classes = {
        'pending': 'bg-yellow-400',
        'in_workshop': 'bg-blue-400',
        'qc': 'bg-purple-400',
        'completed': 'bg-green-400',
        'delivered': 'bg-green-600',
        'cancelled': 'bg-red-400'
      }
      return classes[status] || 'bg-gray-400'
    }

    const getTotalItems = (order) => {
      return order.items.reduce((sum, item) => sum + item.quantity, 0)
    }

    const getTotalGrossWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const grossWeight = parseFloat(item.gross_weight) || 0
        return total + (grossWeight * quantity)
      }, 0).toFixed(2)
    }

    const getTotalNetWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const netWeight = parseFloat(item.net_weight) || 0
        return total + (netWeight * quantity)
      }, 0).toFixed(2)
    }



    const getImageUrl = (imageName) => {
      // Use a working placeholder service or return a fallback
      if (!imageName) {
        return 'data:image/svg+xml;base64,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'
      }
      
      // If it's a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      return url
    }

    const toggleOrder = (orderId) => {
      if (expandedOrders.value.includes(orderId)) {
        expandedOrders.value = expandedOrders.value.filter(id => id !== orderId)
      } else {
        expandedOrders.value.push(orderId)
      }
    }

    // Lifecycle
    onMounted(() => {
      // Check if user is a workshop user and redirect them to workshop jobs
      if (userStore.isWorkshop && !userStore.isAdmin) {

        router.push('/workshop/jobs')
        return
      }
      
      loadOrders()
      
      // Auto-refresh orders every 30 seconds to show status updates
      const refreshInterval = setInterval(() => {
        if (!loading.value) {
          loadOrders()
        }
      }, 30000)
      
      // Cleanup interval on component unmount
      onUnmounted(() => {
        clearInterval(refreshInterval)
      })
    })

    return {
      loading,
      error,
      orders,
      pagination,
      activeTab,
      orderLoadingStates,
      placedOrders,
      cancelledOrders,
      tabs,
      filteredOrders,
      loadOrders,
      changePage,
      refreshOrders,
      cancelOrder,
      confirmCancelOrder,
      cancelCancelOrder,
      showCancelConfirm,
      orderToCancel,
      viewOrderDetails,
      formatDate,
      getTotalGrossWeight,
      getTotalNetWeight,
      getStatusColor,
      getStatusText,
      getStatusDescription,
      getNextStep,
      getStatusClasses,
      getTotalItems,
      getImageUrl,
      expandedOrders,
      toggleOrder
    }
  }
}
</script> 